const resolvedFrontendEnvironment = String(process.env.VUE_APP_CURRENT_ENV || 'production').toLowerCase()

const isProductionBuild = resolvedFrontendEnvironment === 'production'

export default {
  methods: {
    findRefByName (refName) {
      let obj = this
      while (obj) {
        if (obj.$refs[refName]) return obj.$refs[refName]
        obj = obj.$parent
      }
      return undefined
    },
    UUID () {
      let dt = new Date().getTime()
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        // eslint-disable-next-line no-mixed-operators
        const r = (dt + Math.random() * 16) % 16 | 0
        dt = Math.floor(dt / 16)
        // eslint-disable-next-line no-mixed-operators
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
      })
    },
    sendActionLog (action, details = null) {
      const isAdminLogsPageAvailable = this.$router.options.routes.find(route => route.path === '/admin-logs')

      if (isAdminLogsPageAvailable) {
        const fullUrl = window.location.href

        this.$http.post('/api/admin-logs', {
          type: 'action',
          action,
          request_url: fullUrl,
          data: details
        })
          .catch(error => {
            console.error('Error logging page action:', error)
          })
      }
    },
    generateChartActions () {
      const _this = this

      const chartBodies = document.querySelectorAll('.p-chart-body')

      chartBodies.forEach((chartBody) => {
        const title = chartBody.previousElementSibling.textContent
        const exportMenuItems =  chartBody.querySelectorAll('.apexcharts-menu-item')

        if (exportMenuItems.length >= 1) {
          const [buttonSVG, buttonPNG, buttonCSV] = exportMenuItems
          buttonSVG.addEventListener('click', () => {
            _this.sendActionLog(`Export SVG ${title} chart from ${_this.$route.name}`)
          })

          buttonPNG.addEventListener('click', () => {
            _this.sendActionLog(`Export PNG ${title} chart from ${_this.$route.name}`)
          })

          buttonCSV.addEventListener('click', () => {
            _this.sendActionLog(`Export CSV ${title} chart from ${_this.$route.name}`)
          })
        }
      })
    },
    invokeMethod (methodName) {
      if (typeof this[methodName] === 'function') {
        this[methodName]()
      }
    },
    isValidJson (data) {
      try {
        JSON.parse(data)
        return true
      } catch (e) {
        return false
      }
    },
    parseJson (data) {
      return this.isValidJson(data) ? JSON.parse(data) : {}
    },
    copyRawToClipboard (data) {
      window.navigator.clipboard.writeText(data)
        .then(() => {
          this.alertSuccess('Copied successfully')
        })
        .catch((err) => {
          this.alertError('Failed to copy to clipboard. Please try again.')
          console.error(err)
        })
    },
    formatAsJson (data) {
      try {
        return JSON.stringify(data, null, 4) // Pretty-print with indentation
      } catch (e) {
        console.error('Failed to format data as JSON', e)
        return data // Return raw data if formatting fails
      }
    },
    refreshFilterElement (object, element, keyId = 'refresh_uuid') {
      this.$set(object[element], keyId, (Math.random() + 1).toString(36).substring(7))
    },
    handleErrors (errors) {
      const errorMsg =
        errors.response && errors.response.data ? (errors.response.data.errors || errors.response.data) : errors.message
      this.alertError(errorMsg)
      this.hideSpinnerAndLoading()
    },
    getTopAdminIds () {
      return [1, 5]
    },
    /**
     * Frontend mirror of backend AccessHelper::canAccessPageByAllowlist
     * - Non-production when enforceOnlyInProduction=true: ignore allowlist -> return true
     * - Production (or when enforcement is forced): allow if allowlist is empty OR current admin is in it
     * - If allowTopAdmins=true, top admins bypass in any environment (uses this.isTopAdmin)
     *
     * Normal permission checks happen elsewhere.
     */
    canAccessPageByAllowlist (allowlistAdminIds, { enforceOnlyInProduction = true, allowTopAdmins = true } = {}) {
      const adminInformation = this.$store.state.auth.AppActiveAdmin
      if (!adminInformation) return false

      // Top-admin bypass (any env) if enabled
      if (allowTopAdmins && this.isTopAdmin) return true

      // Super admin by role (default 1, or your const)
      const isSuperAdmin = Number(adminInformation.role) === Number(window.CONST_ADMIN_ROLE_SUPER_ADMIN ?? 1)

      // Non-production super-admin bypass (mirrors backend change)
      if (enforceOnlyInProduction && !isProductionBuild && isSuperAdmin) return true

      // Invalid/empty list => not gated (warn only in non-prod)
      if (!Array.isArray(allowlistAdminIds) || allowlistAdminIds.length === 0) {
        if (!isProductionBuild) {
          // eslint-disable-next-line no-console
          console.warn('canAccessPageByAllowlist: allowlistAdminIds should be a non-empty array; got', allowlistAdminIds)
        }
        return isSuperAdmin
      }

      const currentAdminId = Number(adminInformation.id)
      if (!Number.isFinite(currentAdminId)) return false

      const normalizedAllowlist = new Set(
        allowlistAdminIds.map(Number).filter(Number.isFinite)
      )

      return normalizedAllowlist.has(currentAdminId)
    },
    canAccessPageByPermissionModules (requiredModules, { enforceOnlyInProduction = true, allowTopAdmins = true } = {}) {
      const adminInformation = this.$store.state.auth.AppActiveAdmin
      if (!adminInformation) return false

      // Top-admin bypass (any env) if enabled
      if (allowTopAdmins && this.isTopAdmin) {
        return true
      }

      const superAdminRoleId = Number(window.CONST_ADMIN_ROLE_SUPER_ADMIN || 1)
      const isSuperAdmin = Number(adminInformation.role) === superAdminRoleId

      // Non-production super-admin bypass (mirrors backend)
      if (enforceOnlyInProduction && !isProductionBuild && isSuperAdmin) {
        return true
      }

      const adminModules = new Set(
        (adminInformation.permissions?.modules || []).map(String)
      )

      return (requiredModules || []).some(key => adminModules.has(String(key)))
    }
  },
  computed: {
    isProductionBuild () {
      return isProductionBuild
    },
    isQA () {
      const currentLoggedInAdmin = this.$store.state.auth.AppActiveAdmin

      return [11, 46].includes(currentLoggedInAdmin.id) ||
        currentLoggedInAdmin.email.includes('gabriel@primeinsights.com')
    },
    isTopAdmin () {
      const currentUserId = this.$store.state.auth.AppActiveAdmin.id

      return [1, 5].includes(currentUserId)
    }
  },
  created () {
    if (!window.VueComp) window.VueComp = {
      Others: []
    }

    const compName = this.$options.name
    if (typeof compName === 'undefined') return
    if (!window.VueComp[compName]) window.VueComp[compName] = this
    else window.VueComp.Others.push(this)
  }
}
